import { Loader2 } from "lucide-react";

export const STATUS_LABELS = {
    NEW: "New",
    CONTACTED: "Contacted",
    MEDICAL_INFORMATION_RECEIVED: "Medical info received",
    CONSULTATION_PENDING: "Consultation pending",
    CONSULTATION_COMPLETED: "Consultation completed",
    TREATMENT_PLANNING: "Treatment planning",
    TRAVEL_PLANNING: "Travel planning",
    ARRIVED_IN_INDIA: "Arrived in India",
    TREATMENT: "Treatment",
    RECOVERY: "Recovery",
    FOLLOW_UP: "Follow-up",
    CLOSED: "Closed",
};

export const CASE_STATUSES = Object.keys(STATUS_LABELS);
export const PRIORITIES = ["LOW", "MEDIUM", "HIGH", "URGENT"];

const STATUS_BADGE = {
    NEW: "bg-slate-800/90 text-slate-200 border-slate-700/80",
    CONTACTED: "bg-blue-950/90 text-blue-300 border-blue-800/70",
    MEDICAL_INFORMATION_RECEIVED: "bg-indigo-950/90 text-indigo-300 border-indigo-800/70",
    CONSULTATION_PENDING: "bg-amber-950/90 text-amber-300 border-amber-800/70",
    CONSULTATION_COMPLETED: "bg-cyan-950/90 text-cyan-300 border-cyan-800/70",
    TREATMENT_PLANNING: "bg-purple-950/90 text-purple-300 border-purple-800/70",
    TRAVEL_PLANNING: "bg-sky-950/90 text-sky-300 border-sky-800/70",
    ARRIVED_IN_INDIA: "bg-emerald-950/90 text-emerald-300 border-emerald-800/70",
    TREATMENT: "bg-teal-950/90 text-teal-300 border-teal-800/70",
    RECOVERY: "bg-green-950/90 text-green-300 border-green-800/70",
    FOLLOW_UP: "bg-violet-950/90 text-violet-300 border-violet-800/70",
    CLOSED: "bg-zinc-800/90 text-zinc-400 border-zinc-700/80",
};

const PRIORITY_BADGE = {
    URGENT: "bg-red-950/90 text-red-300 border-red-800/80",
    HIGH: "bg-orange-950/90 text-orange-300 border-orange-800/80",
    MEDIUM: "bg-amber-950/70 text-amber-300 border-amber-800/60",
    LOW: "bg-slate-800/80 text-slate-300 border-slate-700/60",
};

export const Badge = ({ children, className = "" }) => (
    <span
        className={`inline-flex items-center whitespace-nowrap rounded-md border px-2 py-0.5 text-[11px] font-semibold tracking-wide ${className}`}
    >
        {children}
    </span>
);

export const StatusBadge = ({ status }) => (
    <Badge className={STATUS_BADGE[status] || STATUS_BADGE.NEW} data-testid={`status-badge-${status}`}>
        {STATUS_LABELS[status] || status}
    </Badge>
);

export const PriorityBadge = ({ priority }) => (
    <Badge
        className={PRIORITY_BADGE[priority] || PRIORITY_BADGE.LOW}
        data-testid={`priority-badge-${priority}`}
    >
        {priority}
    </Badge>
);

export const Panel = ({ className = "", children }) => (
    <div className={`bg-[#12201D] border border-[#1D322E] rounded-xl ${className}`}>{children}</div>
);

export const PageHead = ({ title, subtitle, actions }) => (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
            <h1 className="text-2xl font-bold tracking-tight text-[#F7F5EF]" data-testid="admin-page-title">
                {title}
            </h1>
            {subtitle && <p className="mt-1 text-sm text-[#A3B8B0]">{subtitle}</p>}
        </div>
        {actions}
    </div>
);

export const Loading = () => (
    <div data-testid="admin-loading" className="flex items-center justify-center gap-3 py-24 text-[#A3B8B0]">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">Loading…</span>
    </div>
);

export const LoadError = ({ message }) => (
    <div data-testid="admin-load-error" className="rounded-xl border border-red-900/60 bg-red-950/40 px-4 py-3 text-sm text-red-300">
        {message}
    </div>
);

export const EmptyState = ({ icon: Icon, title, hint }) => (
    <div data-testid="admin-empty-state" className="py-16 text-center">
        {Icon && <Icon className="w-10 h-10 mx-auto text-[#64716D]" />}
        <p className="mt-4 text-sm font-semibold text-[#A3B8B0]">{title}</p>
        {hint && <p className="mt-1 text-xs text-[#64716D]">{hint}</p>}
    </div>
);

export const fmtDate = (iso) =>
    iso
        ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
        : "—";

export const fmtDateTime = (iso) =>
    iso
        ? new Date(iso).toLocaleString("en-GB", {
              day: "2-digit",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
          })
        : "—";

export const PARTNERSHIP_LABELS = {
    PROSPECT: "Prospect",
    CONTACTED: "Contacted",
    DISCUSSION: "In discussion",
    AGREEMENT_PENDING: "Agreement pending",
    PARTNER: "Partner",
    INACTIVE: "Inactive",
};

const PARTNERSHIP_BADGE = {
    PROSPECT: "bg-slate-800/90 text-slate-300 border-slate-700/80",
    CONTACTED: "bg-blue-950/90 text-blue-300 border-blue-800/70",
    DISCUSSION: "bg-indigo-950/90 text-indigo-300 border-indigo-800/70",
    AGREEMENT_PENDING: "bg-amber-950/90 text-amber-300 border-amber-800/70",
    PARTNER: "bg-emerald-950/90 text-emerald-300 border-emerald-800/70",
    INACTIVE: "bg-zinc-800/90 text-zinc-400 border-zinc-700/80",
};

export const PartnershipBadge = ({ status }) => (
    <Badge
        className={PARTNERSHIP_BADGE[status] || PARTNERSHIP_BADGE.PROSPECT}
        data-testid={`partnership-badge-${status}`}
    >
        {PARTNERSHIP_LABELS[status] || status}
    </Badge>
);

export const REFERRAL_LABELS = {
    NOT_REFERRED: "Not referred",
    REFERRAL_PLANNED: "Referral planned",
    REFERRED: "Referred",
    HOSPITAL_RESPONSE_RECEIVED: "Hospital response received",
    PATIENT_DECISION_PENDING: "Patient decision pending",
    ACCEPTED: "Accepted",
    DECLINED: "Declined",
    CLOSED: "Closed",
};

const REFERRAL_BADGE = {
    NOT_REFERRED: "bg-slate-800/90 text-slate-300 border-slate-700/80",
    REFERRAL_PLANNED: "bg-sky-950/90 text-sky-300 border-sky-800/70",
    REFERRED: "bg-blue-950/90 text-blue-300 border-blue-800/70",
    HOSPITAL_RESPONSE_RECEIVED: "bg-indigo-950/90 text-indigo-300 border-indigo-800/70",
    PATIENT_DECISION_PENDING: "bg-amber-950/90 text-amber-300 border-amber-800/70",
    ACCEPTED: "bg-emerald-950/90 text-emerald-300 border-emerald-800/70",
    DECLINED: "bg-red-950/90 text-red-300 border-red-800/70",
    CLOSED: "bg-zinc-800/90 text-zinc-400 border-zinc-700/80",
};

export const ReferralBadge = ({ status }) => (
    <Badge className={REFERRAL_BADGE[status] || REFERRAL_BADGE.NOT_REFERRED} data-testid={`referral-badge-${status}`}>
        {REFERRAL_LABELS[status] || status}
    </Badge>
);

export const SOURCE_LABELS = {
    WEBSITE: "Website",
    WHATSAPP: "WhatsApp",
    EMAIL: "Email",
    REFERRAL: "Referral",
    HOSPITAL: "Hospital",
    DOCTOR: "Doctor",
    SOCIAL_MEDIA: "Social media",
    MEDICAL_AGENT: "Medical agent",
    OTHER: "Other",
};

export const COMM_TYPE_LABELS = {
    WHATSAPP: "WhatsApp",
    PHONE: "Phone",
    EMAIL: "Email",
    IN_PERSON: "In person",
    OTHER: "Other",
};

export const ENQUIRY_SOURCES = Object.keys(SOURCE_LABELS);
export const REFERRAL_STATUSES = Object.keys(REFERRAL_LABELS);
export const COMMUNICATION_TYPES = Object.keys(COMM_TYPE_LABELS);
export const PARTNERSHIP_STATUSES = Object.keys(PARTNERSHIP_LABELS);

export const SourceBadge = ({ source }) => (
    <Badge className="border-[#1D322E] bg-slate-800/80 text-slate-300" data-testid={`source-badge-${source}`}>
        {SOURCE_LABELS[source] || source || "—"}
    </Badge>
);

export const VisibilityBadge = ({ visible }) => (
    <Badge
        className={visible ? "border-emerald-800/70 bg-emerald-950/60 text-emerald-300" : "border-zinc-700/80 bg-zinc-800/80 text-zinc-400"}
        data-testid={`visibility-badge-${visible ? "public" : "private"}`}
    >
        {visible ? "Public" : "Private"}
    </Badge>
);
